<template>
  <nav class="navigation">
    <ul v-if="totalRows > 0" :class="paginationClasses" :aria-label="ariaLabel">
      <li v-if="!hideGotoEndButtons && !limit" :class="pageClass">
        <a
          :class="{
            disabled: value === 1,
            'page-link': true,
            [prevClass]: true,
            clickable: value !== 1,
          }"
          :aria-label="labelFirstPage"
          :aria-controls="ariaControls"
          :aria-disabled="disabled ? 'true' : 'false'"
          v-html="firstText"
        >
        </a>
      </li>
      <li :class="pageClass">
        <a
          :class="{
            disabled: value === 1,
            'page-link': true,
            [prevClass]: true,
            clickable: value !== 1,
          }"
          :aria-label="labelFirstPage"
          :aria-controls="ariaControls"
          :aria-disabled="disabled ? 'true' : 'false'"
          @click="() => $emit('change', value > 1 ? value - 1 : 1)"
          v-html="prevText"
        >
        </a>
      </li>
      <li v-for="(page, index) in pages" :key="index" :class="pageClass">
        <a
          v-if="page === '...' && !hideEllipsis"
          :class="{
            'page-link': true,
            active: page === value,
            clickable: false,
          }"
          :aria-label="labelPage"
          :aria-controls="ariaControls"
          :aria-disabled="disabled ? 'true' : 'false'"
          @click="() => $emit('change', value > 1 ? value - 1 : 1)"
        >
          {{ page }}
        </a>
        <a
          v-else-if="page !== '...'"
          :class="{
            'page-link': true,
            clickable: true,
            active: page === value,
          }"
          :aria-label="labelPage"
          :aria-controls="ariaControls"
          :aria-disabled="disabled ? 'true' : 'false'"
          @click="() => $emit('change', page)"
        >
          {{ page }}
        </a>
      </li>

      <li :class="pageClass">
        <a
          :disable="value === totalPages"
          :class="{
            disabled: value === totalPages,
            'page-link': true,
            [nextClass]: true,
            clickable: value !== totalPages,
          }"
          :aria-label="labelLastPage"
          :aria-controls="ariaControls"
          :aria-disabled="disabled ? 'true' : 'false'"
          @click="() => $emit('change', value < totalPages ? value + 1 : totalPages)"
          v-html="nextText"
        >
        </a>
      </li>
      <li v-if="!hideGotoEndButtons && !limit" :class="pageClass">
        <a
          :disabled="value === totalPages"
          :class="{
            'page-link': true,
            disabled: value === totalPages,
            [nextClass]: true,
            clickable: value !== totalPages,
          }"
          :aria-label="labelLastPage"
          :aria-controls="ariaControls"
          :aria-disabled="disabled ? 'true' : 'false'"
          v-html="lastText"
        >
        </a>
      </li>
    </ul>
  </nav>
</template>
<script>
export default {
  name: "BPagination",
  props: {
    tag: {
      type: String,
      default: "ul",
    },
    align: {
      type: String,
      default: "left",
    },
    ariaControls: {
      type: String,
      default: "",
    },
    ariaLabel: {
      type: String,
      default: "pagination",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    ellipsisClass: {
      type: String,
      default: "ellipsis",
    },
    ellipsisText: {
      type: String,
      default: "...",
    },
    hideEllipsis: {
      type: Boolean,
      default: false,
    },
    hideGotoEndButtons: {
      type: Boolean,
      default: false,
    },
    firstNumber: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: "md",
    },
    limit: {
      type: Number,
    },
    totalRows: {
      type: Number,
      default: 0,
    },
    perPage: {
      type: Number,
      default: 10,
    },
    value: {
      type: Number,
      default: 1,
    },
    firstText: {
      type: String,
      default: "&laquo;",
    },
    prevText: {
      type: String,
      default: "&lsaquo;",
    },
    nextText: {
      type: String,
      default: "&rsaquo;",
    },
    lastText: {
      type: String,
      default: "&raquo;",
    },
    label: {
      type: String,
      default: "pagination",
    },
    labelFirstPage: {
      type: String,
      default: "Go to first page",
    },
    labelPrevPage: {
      type: String,
      default: "Go to previous page",
    },
    labelNextPage: {
      type: String,
      default: "Go to next page",
    },
    labelLastPage: {
      type: String,
      default: "Go to last page",
    },
    labelPage: {
      type: String,
      default: "Go to page",
    },
    lastClass: {
      type: String,
      default: "last",
    },
    nextClass: {
      type: String,
      default: "next",
    },
    prevClass: {
      type: String,
      default: "prev",
    },
    pageClass: {
      type: String,
      default: "page-item",
    },
  },
  emits: ["change"],
  data() {
    return {
      totalPages: Math.ceil(this.totalRows / this.perPage),
    };
  },
  computed: {
    pages() {
      const pages = [];
      if (this.limit === undefined) {
        return this.totalPages;
      }

      if (this.totalPages <= this.limit) {
        // If total pages is less than or equal to limit, show all pages
        return Array.from({ length: this.totalPages }, (_, i) => i + 1);
      }
      // Always include first page
      pages.push(1);
      // Calculate start and end page numbers
      let start = Math.max(2, this.value - Math.floor(this.limit / 2));
      const end = Math.min(this.totalPages - 1, start + this.limit - 1); // Changed here
      // Adjust start if we're at the end of the page range
      if (this.value > this.totalPages - Math.floor(this.limit / 2)) {
        start = this.totalPages - this.limit + 1;
      }
      // Add ellipsis and pages
      if (start > 2) pages.push("...");
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (end < this.totalPages - 1) pages.push("...");
      // Always include last page
      pages.push(this.totalPages);

      return pages;
    },
    paginationClasses() {
      return {
        pagination: true,
        "m-0": true,
        "justify-content-center": this.align === "center",
        "justify-content-end": this.align === "right",
        "justify-content-start": this.align === "left",
        "pagination-sm": this.size === "sm",
        "pagination-lg": this.size === "lg",
      };
    },
  },
  watch: {
    totalRows() {
      this.totalPages = Math.ceil(this.totalRows / this.perPage);
    },
  },
};
</script>
